/**
 * languageClient.ts
 * Canonical D1 API Client for Language Learning Module.
 * Completely isolates user learning progress to Cloudflare D1.
 */

import { apiGet, apiPost, apiPut, apiDelete } from './api';
import {
  LanguageProfile,
  TargetLanguage,
  LanguageDailyPlan,
  LanguagePracticeSession,
  LanguageSkillProgress,
  LanguageResource,
  LanguageRecommendationHistory,
  LanguageSkillAnalytics,
  DifficultyFeedback,
  UsefulnessFeedback,
  AiExerciseData,
  AiFeedbackData,
  LanguageSkillType,
  LanguageLevel
} from '../types';

export interface LanguageBootstrapPayload {
  profile: LanguageProfile | null;
  targets: TargetLanguage[];
  dailyPlans: LanguageDailyPlan[];
  skillProgress: LanguageSkillProgress[];
  resources: LanguageResource[];
  recommendationHistory?: LanguageRecommendationHistory[];
  analytics?: LanguageSkillAnalytics | null;
}

export async function fetchLanguageBootstrap(dateStr?: string): Promise<LanguageBootstrapPayload> {
  const url = dateStr ? `/api/language/bootstrap?date=${encodeURIComponent(dateStr)}` : '/api/language/bootstrap';
  const res = await apiGet<LanguageBootstrapPayload>(url);
  if (!res.success || !res.data) {
    throw new Error(res.error || 'Failed to load language data from cloud database');
  }
  return res.data;
}

export async function saveLanguageProfile(data: {
  source_language: string;
  daily_availability_minutes: number;
  target_languages?: Array<{ code: string; name: string; level: LanguageLevel; daily_goal_minutes?: number }>;
}): Promise<LanguageProfile> {
  const res = await apiPost<LanguageProfile>('/api/language/profile', data);
  if (!res.success || !res.data) {
    throw new Error(res.error || 'Failed to save language profile');
  }
  return res.data;
}

export async function saveTargetLanguage(data: {
  id?: string;
  language_code: string;
  language_name: string;
  level: LanguageLevel;
  daily_goal_minutes: number;
  assessed_level?: LanguageLevel;
  level_notes?: string;
  weak_areas?: string;
}): Promise<TargetLanguage> {
  const res = await apiPost<TargetLanguage>('/api/language/targets', data);
  if (!res.success || !res.data) {
    throw new Error(res.error || 'Failed to save target language');
  }
  return res.data;
}

export async function deleteTargetLanguage(targetId: string): Promise<void> {
  const res = await apiDelete(`/api/language/targets/${encodeURIComponent(targetId)}`);
  if (!res.success) {
    throw new Error(res.error || 'Failed to delete target language');
  }
}

export async function discoverDailyPlan(params: {
  target_language_id: string;
  plan_date?: string;
  force_refresh?: boolean;
}): Promise<{ plan: LanguageDailyPlan; analytics: LanguageSkillAnalytics }> {
  const res = await apiPost<{ plan: LanguageDailyPlan; analytics: LanguageSkillAnalytics }>(
    '/api/language/plans/discover',
    params
  );
  if (!res.success || !res.data) {
    throw new Error(res.error || 'Failed to discover smart daily plan');
  }
  return res.data;
}

export async function completePlanActivity(params: {
  plan_id: string;
  item_id: string;
  skill: LanguageSkillType;
  actual_minutes: number;
  difficulty_feedback?: DifficultyFeedback;
  usefulness_feedback?: UsefulnessFeedback;
  user_notes?: string;
}): Promise<{ plan: LanguageDailyPlan; analytics: LanguageSkillAnalytics }> {
  const res = await apiPost<{ plan: LanguageDailyPlan; analytics: LanguageSkillAnalytics }>(
    '/api/language/activities/complete',
    params
  );
  if (!res.success || !res.data) {
    throw new Error(res.error || 'Failed to complete activity');
  }
  return res.data;
}

export async function fetchLanguageAnalytics(targetId: string): Promise<LanguageSkillAnalytics> {
  const res = await apiGet<LanguageSkillAnalytics>(`/api/language/analytics/${encodeURIComponent(targetId)}`);
  if (!res.success || !res.data) {
    throw new Error(res.error || 'Failed to fetch language analytics');
  }
  return res.data;
}

export async function fetchRecommendationHistory(targetId?: string, limit: number = 20): Promise<LanguageRecommendationHistory[]> {
  const url = targetId
    ? `/api/language/recommendations?targetId=${encodeURIComponent(targetId)}&limit=${limit}`
    : `/api/language/recommendations?limit=${limit}`;
  const res = await apiGet<LanguageRecommendationHistory[]>(url);
  if (!res.success || !res.data) {
    return [];
  }
  return res.data;
}

export async function updateDailyPlan(planId: string, data: {
  listening_minutes: number;
  speaking_minutes: number;
  reading_minutes: number;
  writing_minutes: number;
  notes?: string;
}): Promise<LanguageDailyPlan> {
  const res = await apiPut<LanguageDailyPlan>(`/api/language/plans/${encodeURIComponent(planId)}`, data);
  if (!res.success || !res.data) {
    throw new Error(res.error || 'Failed to update daily plan');
  }
  return res.data;
}

export async function recordPracticeSession(session: {
  target_language_id: string;
  skill: LanguageSkillType;
  duration_minutes: number;
  exercise_type?: string;
  title: string;
  content_prompt?: string;
  user_response?: string;
  ai_feedback?: string;
  score?: number;
  session_date?: string;
}): Promise<LanguagePracticeSession> {
  const res = await apiPost<LanguagePracticeSession>('/api/language/sessions', session);
  if (!res.success || !res.data) {
    throw new Error(res.error || 'Failed to save practice session');
  }
  return res.data;
}

export async function fetchPracticeHistory(targetId?: string, limit: number = 30): Promise<LanguagePracticeSession[]> {
  const url = targetId 
    ? `/api/language/sessions?targetId=${encodeURIComponent(targetId)}&limit=${limit}`
    : `/api/language/sessions?limit=${limit}`;
  const res = await apiGet<LanguagePracticeSession[]>(url);
  if (!res.success || !res.data) {
    return [];
  }
  return res.data;
}

export async function addCustomResource(resource: {
  target_language_code: string;
  skill: LanguageSkillType | 'all';
  title: string;
  description?: string;
  source_name: string;
  url: string;
  difficulty_level?: string;
}): Promise<LanguageResource> {
  const res = await apiPost<LanguageResource>('/api/language/resources', resource);
  if (!res.success || !res.data) {
    throw new Error(res.error || 'Failed to add learning resource');
  }
  return res.data;
}

export async function deleteResource(id: string): Promise<void> {
  const res = await apiDelete(`/api/language/resources/${encodeURIComponent(id)}`);
  if (!res.success) {
    throw new Error(res.error || 'Failed to delete resource');
  }
}

export async function generateAiExercise(params: {
  target_language: string;
  source_language: string;
  skill: LanguageSkillType;
  level: LanguageLevel;
  topic?: string;
}): Promise<AiExerciseData> {
  const res = await apiPost<AiExerciseData>('/api/language/ai/exercise', params);
  if (!res.success || !res.data) {
    throw new Error(res.error || 'Failed to generate AI exercise');
  }
  return res.data;
}

export async function submitForAiFeedback(params: {
  target_language: string;
  source_language: string;
  skill: LanguageSkillType;
  prompt?: string;
  user_submission: string;
}): Promise<AiFeedbackData> {
  const res = await apiPost<AiFeedbackData>('/api/language/ai/feedback', params);
  if (!res.success || !res.data) {
    throw new Error(res.error || 'Failed to get AI feedback');
  }
  return res.data;
}
